import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Stack, Avatar, Typography, IconButton, Tooltip, CircularProgress } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { toast } from 'react-toastify';
import PageHeader from '../../components/PageHeader';
import SearchBar from '../../components/SearchBar';
import DataTable from '../../components/DataTable';
import StatusChip from '../../components/StatusChip';
import ConfirmDialog from '../../components/ConfirmDialog';
import { getBlogs, deleteBlog } from '../../api/endpoints/blog';
import AddBlogModal from './AddBlogModal';

const BlogList = () => {
  const navigate = useNavigate();
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const fetchBlogs = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page: page + 1, limit: rowsPerPage };
      if (debouncedSearch) params.search = debouncedSearch;
      const res = await getBlogs(params);
      setBlogs(res.data.data || []);
      setTotal(res.data.pagination?.total || 0);
    } catch (err) {
      if (err.response?.status !== 404) toast.error(err.response?.data?.message || 'Failed to load blogs');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, debouncedSearch]);

  useEffect(() => { fetchBlogs(); }, [fetchBlogs]);

  const handleDelete = async () => {
    try {
      await deleteBlog(deleteId);
      toast.success('Blog deleted');
      setDeleteId(null);
      fetchBlogs();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete blog');
    }
  };

  const columns = [
    { id: 'image', label: 'Image', render: (row) => (
      <Avatar src={row.image} variant="rounded" sx={{ width: 44, height: 44 }} />
    )},
    { id: 'title', label: 'Title', sortable: true, render: (row) => (
      <Box>
        <Typography variant="body2" fontWeight={500}>{row.title}</Typography>
        <Typography variant="caption" color="text.secondary">{row.category?.name || row.category}</Typography>
      </Box>
    )},
    { id: 'category', label: 'Category', render: (row) => row.category?.name || row.category || '-' },
    { id: 'status', label: 'Status', render: (row) => <StatusChip status={row.isActive ? 'active' : 'inactive'} /> },
    { id: 'actions', label: '', render: (row) => (
      <Stack direction="row">
        <Tooltip title="Edit"><IconButton size="small" onClick={() => navigate(`/blogs/${row._id}/edit`)}><EditIcon fontSize="small" /></IconButton></Tooltip>
        <Tooltip title="Delete"><IconButton size="small" onClick={() => setDeleteId(row._id)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
      </Stack>
    )},
  ];

  return (
    <Box>
      <PageHeader title="Blog Posts" subtitle="Manage your spiritual blog posts" actionLabel="Add Blog" onAction={() => setAddOpen(true)} />
      <Stack direction="row" spacing={2} sx={{ mb: 2 }} alignItems="center">
        <SearchBar value={search} onChange={setSearch} placeholder="Search blogs..." sx={{ minWidth: 300 }} />
      </Stack>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
      ) : (
        <DataTable
          columns={columns} rows={blogs} page={page} rowsPerPage={rowsPerPage}
          total={total} onPageChange={setPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        />
      )}
      <ConfirmDialog open={!!deleteId} title="Delete Blog" message="Are you sure you want to delete this blog post?" onConfirm={handleDelete} onCancel={() => setDeleteId(null)} />
      <AddBlogModal open={addOpen} onClose={() => setAddOpen(false)} onSaved={fetchBlogs} />
    </Box>
  );
};

export default BlogList;
